import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState
} from 'react';
import toast from 'react-hot-toast';
import { customerPortalService, default as customerPortalApi } from '../services/customerPortalApi';
import { useNavigate } from 'react-router-dom';
import {
  registerServiceWorker,
  getOrCreatePushSubscription,
  serializePushSubscription,
  getPushSupportStatus,
  setVapidPublicKey,
  hasPushSubscriptions
} from '../utils/pushService';

const CustomerNotificationContext = createContext({
  notifications: [],
  unreadCount: 0,
  loading: false,
  preferences: null,
  refresh: () => {},
  markAsRead: async () => {},
  markAllAsRead: async () => {},
  deleteNotification: async () => {},
  openNotificationPanel: () => {},
  ensurePushSubscription: async () => ({ success: false }),
  pushSupport: { supported: false, reason: 'unknown' },
  hasServerPushSubscription: false
});

export const CustomerNotificationProvider = ({ children }) => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [preferences, setPreferences] = useState(null);
  const [pushSupport, setPushSupport] = useState({ supported: false, reason: 'unknown' });
  const [vapidReady, setVapidReady] = useState(false);
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const initialLoadRef = useRef(true);
  const lastSeenIdRef = useRef(null);
  const pollingRef = useRef(null);
  const serviceWorkerReadyRef = useRef(false);
  const metaLoadedRef = useRef(false);
  const notificationFetchInProgressRef = useRef(false);
  const pushSyncInProgressRef = useRef(false);
  const pushSyncedRef = useRef(false);

  const LAST_SEEN_STORAGE_KEY = 'customer-portal-last-seen-notification-id';

  const readLastSeenIdFromStorage = useCallback(() => {
    try {
      if (typeof window === 'undefined') return null;
      const stored = localStorage.getItem(LAST_SEEN_STORAGE_KEY);
      if (!stored) return null;
      const parsed = parseInt(stored, 10);
      return Number.isFinite(parsed) ? parsed : null;
    } catch (_) {
      return null;
    }
  }, []);

  const writeLastSeenIdToStorage = useCallback((id) => {
    try {
      if (typeof window === 'undefined' || id == null) return;
      localStorage.setItem(LAST_SEEN_STORAGE_KEY, String(id));
    } catch (_) {
      // Sessizce atlanıyor
    }
  }, []);

  // localStorage'dan son görülen bildirim ID'sini yükle
  useEffect(() => {
    const storedId = readLastSeenIdFromStorage();
    if (storedId != null) {
      lastSeenIdRef.current = storedId;
    }
  }, [readLastSeenIdFromStorage]);

  const calculateUnread = useCallback((items = []) => {
    return items.filter((item) => !item.isRead).length;
  }, []);

  const fetchNotifications = useCallback(async (options = {}) => {
    const includeMeta = options?.includeMeta === true;
    const silent = options?.silent === true;
    if (!customerPortalService.isAuthenticated()) return;

    if (notificationFetchInProgressRef.current) {
      return;
    }

    notificationFetchInProgressRef.current = true;
    try {
      if (!silent) {
        setLoading(true);
      }
      
      let notificationRes = { data: { success: true, data: { items: [] } } };
      const shouldLoadMeta = includeMeta || !metaLoadedRef.current;
      let prefRes = null;
      let vapidRes = null;
      
      try {
        notificationRes = await customerPortalService.get('/notifications?limit=20');
      } catch (err) {
        if (err?.response?.status !== 401) {
          // 401 dışındaki hatalar sessizce atlanıyor
        }
      }
      
      if (shouldLoadMeta) {
        try {
          prefRes = await customerPortalService.get('/notifications/preferences');
        } catch (err) {
          if (err?.response?.status !== 401) {
            // 401 dışındaki hatalar sessizce atlanıyor
          }
        }

        try {
          vapidRes = await customerPortalService.get('/notifications/vapid-key');
        } catch (err) {
          // VAPID key hatası önemli değil
        }
      }
      
      const items = notificationRes.data?.data?.items || [];
      setNotifications(items);
      setUnreadCount(calculateUnread(items));
      if (shouldLoadMeta) {
        setPreferences(prefRes?.data?.data || null);
        const key = vapidRes?.data?.data?.publicKey || vapidRes?.data?.publicKey || process.env.REACT_APP_WEB_PUSH_PUBLIC_KEY || '';
        setVapidPublicKey(key);
        const supportStatus = getPushSupportStatus();
        setPushSupport(supportStatus);
        setVapidReady(!!key);
        metaLoadedRef.current = true;
      }
      handleIncomingNotifications(items);
    } catch (error) {
      // Sessizce atlanıyor
    } finally {
      notificationFetchInProgressRef.current = false;
      if (!silent) {
        setLoading(false);
      }
    }
  }, [calculateUnread]);

  const markAsRead = useCallback(
    async (notificationId) => {
      try {
        await customerPortalApi.put(`/notifications/${notificationId}/read`);
        setNotifications((prev) =>
          prev.map((item) =>
            item.id === notificationId ? { ...item, isRead: true, readAt: new Date().toISOString() } : item
          )
        );
        setUnreadCount((prev) => Math.max(prev - 1, 0));
      } catch (error) {
        toast.error('Bildirim güncellenemedi', { duration: 3000 });
      }
    },
    []
  );

  const markAllAsRead = useCallback(async () => {
    try {
      await customerPortalApi.put('/notifications/read-all');
      setNotifications((prev) =>
        prev.map((item) => ({ ...item, isRead: true, readAt: new Date().toISOString() }))
      );
      setUnreadCount(0);
      toast.success('Tüm bildirimler okundu işaretlendi', { duration: 3000 });
    } catch (error) {
      toast.error('Bildirimler güncellenemedi', { duration: 3000 });
    }
  }, []);

  const deleteNotification = useCallback(async (notificationId) => {
    try {
      await customerPortalApi.delete(`/notifications/${notificationId}`);
      setNotifications((prev) => prev.filter((item) => item.id !== notificationId));
      setUnreadCount((prev) => Math.max(prev - 1, 0));
    } catch (error) {
      toast.error('Bildirim silinemedi', { duration: 3000 });
    }
  }, []);

  const openNotificationPanel = useCallback(() => {
    setIsPanelOpen(true);
  }, []);

  const showToastForNotification = useCallback((notification) => {
    if (!notification || !notification.title) return;
    
    // Önceki toast'ları temizle (takılmayı önlemek için)
    toast.dismiss();
    
    try {
      const toastId = toast.custom(
        (t) => {
          if (!t || !t.id) return null;
          
          // Otomatik kapanma için timeout
          const autoCloseTimeout = setTimeout(() => {
            try {
              toast.dismiss(t.id);
            } catch (error) {
              // Sessizce atlanıyor
            }
          }, 4000); // 4 saniye sonra otomatik kapan
          
          const handleClose = (e) => {
            try {
              e?.stopPropagation();
              clearTimeout(autoCloseTimeout);
              toast.dismiss(t.id);
            } catch (error) {
              // Sessizce atlanıyor
            }
          };
          const handleClick = () => {
            try {
              clearTimeout(autoCloseTimeout);
              toast.dismiss(t.id);
              if (notification.metadata?.redirectUrl) {
                navigate(notification.metadata.redirectUrl);
              } else {
                openNotificationPanel();
              }
            } catch (error) {
              // Sessizce atlanıyor
            }
          };
          return (
            <div className="pointer-events-auto max-w-sm w-full bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 p-3 relative">
              <button
                onClick={handleClose}
                className="absolute top-2 right-2 p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition z-10"
                aria-label="Kapat"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
              <div
                onClick={handleClick}
                className="cursor-pointer pr-6"
              >
                <p className="text-xs text-gray-400 dark:text-gray-500">Yeni bildirim</p>
                <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 mt-1">{notification.title || ''}</p>
                <p className="text-xs text-gray-600 dark:text-gray-300 mt-1 whitespace-pre-line">
                  {notification.body || ''}
                </p>
              </div>
            </div>
          );
        },
        { 
          duration: 4000,
          id: `notification-${notification.id || Date.now()}` // Unique ID ile çift toast önle
        }
      );
      
      // Güvenlik için maksimum 5 saniye sonra zorla kapat
      setTimeout(() => {
        try {
          toast.dismiss(toastId);
        } catch (error) {
          // Sessizce atlanıyor
        }
      }, 5000);
    } catch (error) {
      // Sessizce atlanıyor
    }
  }, [navigate, openNotificationPanel]);

  const handleIncomingNotifications = useCallback(
    (items) => {
      if (!items.length) return;
      const newestId = Math.max(...items.map((item) => Number(item.id) || 0));
      if (!Number.isFinite(newestId) || newestId <= 0) return;

      if (lastSeenIdRef.current == null) {
        const storedId = readLastSeenIdFromStorage();
        if (storedId != null) {
          lastSeenIdRef.current = storedId;
        }
      }

      if (initialLoadRef.current) {
        initialLoadRef.current = false;
        lastSeenIdRef.current = Math.max(lastSeenIdRef.current || 0, newestId);
        writeLastSeenIdToStorage(lastSeenIdRef.current);
        return;
      }

      const lastSeen = lastSeenIdRef.current || 0;
      const newOnes = items.filter((item) => Number(item.id) > lastSeen);
      if (newOnes.length) {
        lastSeenIdRef.current = newestId;
        writeLastSeenIdToStorage(newestId);
        newOnes.reverse().forEach(showToastForNotification);
      }
    },
    [showToastForNotification, readLastSeenIdFromStorage, writeLastSeenIdToStorage]
  );

  const ensurePushSubscription = useCallback(async () => {
    const loadingToastId = 'customer-push-enable-loading';
    if (pushSyncInProgressRef.current) {
      return { success: false };
    }

    pushSyncInProgressRef.current = true;
    try {
      toast.loading('Push bildirimleri etkinleştiriliyor...', {
        id: loadingToastId,
        duration: 15000
      });

      if (!vapidReady) {
        toast.error('Push anahtarları hazır değil; sayfayı yenileyip tekrar deneyin.', {
          id: loadingToastId,
          duration: 4000
        });
        return { success: false };
      }

      const registration = await registerServiceWorker();
      serviceWorkerReadyRef.current = true;

      if (!registration?.pushManager) {
        toast.error('Push Manager bu tarayıcıda desteklenmiyor.', {
          id: loadingToastId,
          duration: 4000
        });
        return { success: false };
      }

      if (typeof window !== 'undefined' && 'Notification' in window && Notification.permission === 'default') {
        toast.loading('Bildirim izni isteniyor...', {
          id: loadingToastId,
          duration: 8000
        });
      }

      const subscription = await getOrCreatePushSubscription(registration);
      const subscriptionData = serializePushSubscription(subscription);
      const subscribeResponse = await customerPortalService.post('/notifications/push-subscribe', {
        subscription: subscriptionData
      });

      if (!subscribeResponse?.data?.success) {
        throw new Error(subscribeResponse?.data?.message || 'Push subscription kaydedilemedi');
      }
      if (subscribeResponse?.data?.data) {
        setPreferences(subscribeResponse.data.data);
      }

      const deviceTypeText = subscriptionData.metadata?.deviceType === 'mobile'
        ? 'mobil cihazda'
        : 'web tarayıcıda';
      toast.success(`Push bildirimleri ${deviceTypeText} etkinleştirildi`, {
        id: loadingToastId,
        duration: 3000
      });

      pushSyncedRef.current = true;
      fetchNotifications({ includeMeta: true });
      return { success: true };
    } catch (error) {
      toast.error(error?.message || 'Push bildirimi etkinleştirilemedi', {
        id: loadingToastId,
        duration: 4000
      });
      return { success: false, error };
    } finally {
      pushSyncInProgressRef.current = false;
    }
  }, [fetchNotifications, vapidReady]);

  const syncPushSubscription = useCallback(async () => {
    if (!customerPortalService.isAuthenticated() || !vapidReady) return { success: false };
    if (pushSyncInProgressRef.current) return { success: false };
    if (typeof window === 'undefined' || !('Notification' in window)) return { success: false };
    if (Notification.permission !== 'granted') return { success: false };

    const supportStatus = getPushSupportStatus();
    if (!supportStatus.supported) return { success: false };

    pushSyncInProgressRef.current = true;
    try {
      const registration = await registerServiceWorker();
      serviceWorkerReadyRef.current = true;
      if (!registration?.pushManager) return { success: false };

      const subscription = await getOrCreatePushSubscription(registration);
      const subscriptionData = serializePushSubscription(subscription);
      const subscribeResponse = await customerPortalService.post('/notifications/push-subscribe', {
        subscription: subscriptionData
      });
      if (subscribeResponse?.data?.data) {
        setPreferences(subscribeResponse.data.data);
      }
      pushSyncedRef.current = true;
      return { success: true };
    } catch (error) {
      // Sessiz sync
      return { success: false, error };
    } finally {
      pushSyncInProgressRef.current = false;
    }
  }, [vapidReady]);

  useEffect(() => {
    if (!customerPortalService.isAuthenticated() || !vapidReady || !metaLoadedRef.current) return;
    if (pushSyncedRef.current && hasPushSubscriptions(preferences)) return;

    const timeoutId = setTimeout(() => {
      syncPushSubscription();
    }, 800);

    return () => clearTimeout(timeoutId);
  }, [vapidReady, preferences, syncPushSubscription]);

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      if (!customerPortalService.isAuthenticated() || !vapidReady) return;
      if (typeof window === 'undefined' || !('Notification' in window)) return;
      if (Notification.permission !== 'granted') return;
      if (!hasPushSubscriptions(preferences)) {
        syncPushSubscription();
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [vapidReady, preferences, syncPushSubscription]);

  useEffect(() => {
    if (!customerPortalService.isAuthenticated()) {
      setNotifications([]);
      setUnreadCount(0);
      setPreferences(null);
      metaLoadedRef.current = false;
      initialLoadRef.current = true;
      lastSeenIdRef.current = readLastSeenIdFromStorage();
      pushSyncedRef.current = false;
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
      return;
    }

    if (lastSeenIdRef.current == null) {
      lastSeenIdRef.current = readLastSeenIdFromStorage();
    }
    initialLoadRef.current = true;
    
    const delay = 200;
    const timeoutId = setTimeout(() => {
      if (!customerPortalService.isAuthenticated()) return;
      
      fetchNotifications({ includeMeta: true });
      
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
      pollingRef.current = setInterval(() => {
        if (customerPortalService.isAuthenticated()) {
          fetchNotifications({ silent: true });
        }
      }, 30000);
    }, delay);
    
    return () => {
      clearTimeout(timeoutId);
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    };
  }, [fetchNotifications, readLastSeenIdFromStorage]);

  const hasServerPushSubscription = hasPushSubscriptions(preferences);

  const value = {
    notifications,
    unreadCount,
    loading,
    preferences,
    refresh: () => fetchNotifications({ includeMeta: true }),
    markAsRead,
    markAllAsRead,
    deleteNotification,
    openNotificationPanel,
    ensurePushSubscription,
    pushSupport,
    hasServerPushSubscription,
    isPanelOpen,
    setIsPanelOpen
  };

  return (
    <CustomerNotificationContext.Provider value={value}>
      {children}
    </CustomerNotificationContext.Provider>
  );
};

export const useCustomerNotifications = () => {
  const context = useContext(CustomerNotificationContext);
  if (!context) {
    throw new Error('useCustomerNotifications must be used within CustomerNotificationProvider');
  }
  return context;
};
